import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import axios from 'axios';
import {
    authRequestInterceptor,
    createAuthResponseRejectionHandler,
} from './authInterceptors';

/**
 * Builds (and memoises) a configured axios instance for talking to the backend.
 *
 * `axiosConfig.baseURL` is required — the caller (a resource service) is
 * responsible for sourcing it from the `ApiHandler` constructor, which in
 * turn receives it from `ApiProvider`'s `baseUrl` prop.
 *
 * The final base URL is `<baseURL>/api/v1` so resource endpoints can be
 * written as relative paths (e.g. `/sales/revenue-summary`).
 *
 * The instance is **cached per resolved base URL** so the auth interceptors are
 * attached exactly once: services call this on every request, and re-attaching
 * a request/response interceptor each time would stack duplicates (and refresh
 * the token N times on a single 401). To bump the API version, change the path
 * segment below — endpoint builders in `handlers/resources/*` remain
 * version-agnostic.
 */
const clientCache = new Map<string, AxiosInstance>();

export const getAxiosClient = (
    axiosConfig: AxiosRequestConfig & { baseURL: string },
): AxiosInstance => {
    if (!axiosConfig.baseURL) {
        throw new Error(
            'getAxiosClient: `baseURL` is required. Set BUN_PUBLIC_API_BASE_URL in your environment so ApiProvider can forward it.',
        );
    }

    const resolvedBaseUrl = new URL('api/v1', axiosConfig.baseURL).toString();

    const cached = clientCache.get(resolvedBaseUrl);
    if (cached) {
        return cached;
    }

    const axiosClientRaw = axios.create({
        ...axiosConfig,
        baseURL: resolvedBaseUrl,
        withCredentials: true,
    });

    // Two-token auth: pick the device vs seller credential per endpoint on the
    // way out, and recover from a 401 (refresh-and-retry for business calls,
    // re-enroll for device calls) on the way in. See `authInterceptors.ts`.
    axiosClientRaw.interceptors.request.use(authRequestInterceptor);
    axiosClientRaw.interceptors.response.use(
        (response) => response,
        createAuthResponseRejectionHandler({ instance: axiosClientRaw }),
    );

    clientCache.set(resolvedBaseUrl, axiosClientRaw);
    return axiosClientRaw;
};
